import { Box, Container, Divider, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import Footer from "../components/common/Footer";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      {children}
    </Box>
  );
}

export default function PrivacyPage() {
  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "background.default" }}>
      <Container maxWidth="md" sx={{ flex: 1, py: 6 }}>
        <Link component={RouterLink} to="/" variant="body2" color="text.secondary" underline="hover" sx={{ display: "inline-block", mb: 4 }}>
          ← Back to īmēra
        </Link>

        <Typography variant="h4" gutterBottom>Privacy Policy</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Last updated: May 2026
        </Typography>

        <Divider sx={{ mb: 4 }} />

        <Section title="Introduction">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            TL Lab ("we", "us", or "our") operates īmēra, a transactional email hub used by TL Lab applications. This policy explains what information īmēra processes and how it is handled.
          </Typography>
        </Section>

        <Section title="Information We Process">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            īmēra processes the following categories of information:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2.5, "& li": { mb: 0.5 } }}>
            <Typography component="li" variant="body2" color="text.secondary"><strong>Email content</strong> — recipient address, sender address, subject, body, and template variables submitted by integrating apps.</Typography>
            <Typography component="li" variant="body2" color="text.secondary"><strong>Delivery metadata</strong> — message IDs, provider responses, and webhook events (delivered, bounced, opened, clicked) used for the audit log.</Typography>
            <Typography component="li" variant="body2" color="text.secondary"><strong>Dashboard accounts</strong> — administrator email addresses and authentication credentials, managed by AWS Cognito.</Typography>
            <Typography component="li" variant="body2" color="text.secondary"><strong>API keys</strong> — hashed credentials issued per consuming app, used to authenticate send requests.</Typography>
          </Box>
        </Section>

        <Section title="How We Use Information">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            We use the information to:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2.5, "& li": { mb: 0.5 } }}>
            <Typography component="li" variant="body2" color="text.secondary">Deliver transactional email through configured providers (Brevo, Resend).</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Provide an audit history of sends and delivery events to administrators.</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Authenticate dashboard users and authorise API requests from consuming apps.</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Diagnose failures and improve reliability.</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, lineHeight: 1.8 }}>
            We do not sell, rent, or share information processed by īmēra with third parties for marketing purposes.
          </Typography>
        </Section>

        <Section title="Email Providers">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            īmēra forwards messages to upstream email providers (Brevo and Resend). Those providers handle final delivery and may retain delivery metadata according to their own policies. īmēra stores message IDs and event status returned by these providers.
          </Typography>
        </Section>

        <Section title="Data Retention">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            Audit records are retained for operational and troubleshooting purposes. Administrators can revoke API keys at any time, which immediately disables sending for that app. We do not retain data beyond what is necessary to operate the service.
          </Typography>
        </Section>

        <Section title="Security">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            We use industry-standard security measures: encrypted transmission (HTTPS), managed authentication via AWS Cognito, hashed API keys, secrets stored in AWS SSM Parameter Store as SecureString, and least-privilege IAM roles. However, no system is completely secure — please use a strong password for your administrator account and treat API keys as secrets.
          </Typography>
        </Section>

        <Section title="Cookies">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            The īmēra dashboard uses session and local storage to maintain authentication state. We do not use third-party advertising cookies.
          </Typography>
        </Section>

        <Section title="Contact Us">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            If you have questions about this privacy policy or your data, please contact us at{" "}
            <Link href="mailto:support@techlead.co.nz" color="primary">support@techlead.co.nz</Link>.
          </Typography>
        </Section>
      </Container>

      <Footer />
    </Box>
  );
}
